import { useRef, useState } from "react";
import { AgeGroup } from "@/data/weekitQuestions";
import { parseBand } from "@/features/assessment-v2/band";
import { decisionCount } from "@/features/assessment-v2/chapters";
import { Button } from "@/components/ui/button";
import {
  ChevronRight,
  Sprout,
  Compass,
  Rocket,
  Sparkles,
  ShieldCheck,
  Users,
  ArrowLeft,
  Check,
} from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import WeKitLogo from "@/components/WeKitLogo";

type StageOption = {
  value: AgeGroup;
  emoji: string;
  title: string;
  age: string;
  description: string;
  icon: React.ReactNode;
};

const STAGES: StageOption[] = [
  {
    value: "12-14",
    emoji: "🌱",
    title: "Explorers",
    age: "12–14 years",
    description: "Discover your interests, strengths and the possibilities ahead.",
    icon: <Sprout className="h-6 w-6" aria-hidden="true" />,
  },
  {
    value: "15-18",
    emoji: "🧭",
    title: "Pathfinders",
    age: "15–18 years",
    description: "Explore careers, subjects and the direction that's right for you.",
    icon: <Compass className="h-6 w-6" aria-hidden="true" />,
  },
  {
    value: "18+",
    emoji: "🚀",
    title: "Trailblazers",
    age: "18+ years",
    description: "Build your career, grow your skills and create your own path.",
    icon: <Rocket className="h-6 w-6" aria-hidden="true" />,
  },
];

const TRAILBLAZER_PERSONAS = [
  "College Student",
  "Graduate",
  "Working Professional",
  "Career Switcher",
  "Entrepreneur",
  "Parent",
  "Mentor",
  "Retired Professional",
] as const;

/**
 * Minutes, derived from the real served-screen count for the band rather
 * than a hard-coded string. The adult flow is 29 decisions in ~13 minutes;
 * every other band is that same pace applied to its own screen count, so
 * the chip can never claim a walk longer than the one we actually serve.
 */
const MINUTES_PER_DECISION = 13 / 29;

function minutesFor(age: AgeGroup | null): number | null {
  if (!age) return null;
  return Math.round(decisionCount(parseBand(age)) * MINUTES_PER_DECISION);
}

/**
 * Roving keyboard behaviour for a `role="radiogroup"`. Arrow keys move the
 * selection (and focus) between options, which is what a screen-reader
 * user expects; Space and Enter select, which native `<button>` already
 * gives us via click. Home/End jump to the ends.
 */
function useRadioKeys<T>(
  values: readonly T[],
  selected: T | null,
  select: (v: T) => void,
  /** Grid width — up/down step by a whole row. 1 for a vertical list. */
  columns = 1,
) {
  const refs = useRef<(HTMLElement | null)[]>([]);

  function move(to: number) {
    const i = (to + values.length) % values.length;
    select(values[i]);
    refs.current[i]?.focus();
  }

  return {
    setRef: (i: number) => (el: HTMLElement | null) => { refs.current[i] = el; },
    /** Roving tabindex: only the selected option (or the first) is tabbable. */
    tabIndexFor: (i: number) =>
      selected == null ? (i === 0 ? 0 : -1) : values[i] === selected ? 0 : -1,
    onKeyDown: (i: number) => (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault(); move(i + columns); break;
        case "ArrowRight":
          e.preventDefault(); move(i + 1); break;
        case "ArrowUp":
          e.preventDefault(); move(i - columns); break;
        case "ArrowLeft":
          e.preventDefault(); move(i - 1); break;
        case "Home":
          e.preventDefault(); move(0); break;
        case "End":
          e.preventDefault(); move(values.length - 1); break;
        case " ":
        case "Enter":
          e.preventDefault(); select(values[i]); break;
        default:
          break;
      }
    },
  };
}

type Phase = "stage" | "persona";

export default function AssessmentAgeSelect({
  onSelect,
  eyebrow,
  title,
  subtitle,
  footer,
}: {
  onSelect: (age: AgeGroup) => void;
  /** Small chip above the headline — e.g. "Payment confirmed". */
  eyebrow?: string;
  /** Overrides the default "Welcome to WeKIT" headline. */
  title?: string;
  /** Overrides the default supporting line. */
  subtitle?: string;
  /** Rendered under the CTA — e.g. a receipt download. */
  footer?: React.ReactNode;
}) {
  const [selected, setSelected] = useState<AgeGroup | null>(null);
  const [phase, setPhase] = useState<Phase>("stage");
  const [persona, setPersona] = useState<string | null>(null);
  const reduce = useReducedMotion();

  const handleContinue = () => {
    if (!selected) return;
    if (selected === "18+") {
      setPhase("persona");
      return;
    }
    onSelect(selected);
  };

  const handlePersonaContinue = () => {
    if (!persona) return;
    try {
      localStorage.setItem("wekit_persona", persona);
    } catch {
      /* ignore */
    }
    onSelect("18+");
  };

  return (
    <div
      className="relative min-h-dvh overflow-hidden"
      style={{ backgroundColor: "#0D1117" }}
    >
      {/* Ambient glows */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <motion.div
          className="absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full blur-[140px]"
          style={{ backgroundColor: "rgba(0, 102, 91, 0.22)" }}
          animate={reduce ? undefined : { scale: [1, 1.08, 1], opacity: [0.7, 0.9, 0.7] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-[-180px] right-[-120px] h-[420px] w-[420px] rounded-full blur-[160px]"
          style={{ backgroundColor: "rgba(255, 95, 31, 0.10)" }}
          animate={reduce ? undefined : { scale: [1, 1.12, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <main className="relative z-10 mx-auto flex min-h-dvh w-full max-w-6xl flex-col items-center px-6 py-6 sm:py-10">
        {phase === "stage" ? (
          <StageScreen
            stages={STAGES}
            selected={selected}
            setSelected={setSelected}
            onContinue={handleContinue}
            reduce={!!reduce}
            eyebrow={eyebrow}
            title={title}
            subtitle={subtitle}
            footer={footer}
          />

        ) : (
          <PersonaScreen
            persona={persona}
            setPersona={setPersona}
            onBack={() => setPhase("stage")}
            onContinue={handlePersonaContinue}
            reduce={!!reduce}
          />
        )}
      </main>
    </div>
  );
}

/* ───────────────── Stage Screen ───────────────── */

function StageScreen({
  stages,
  selected,
  setSelected,
  onContinue,
  reduce,
  eyebrow,
  title,
  subtitle,
  footer,
}: {
  stages: StageOption[];
  selected: AgeGroup | null;
  setSelected: (v: AgeGroup) => void;
  onContinue: () => void;
  reduce: boolean;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  footer?: React.ReactNode;
}) {
  const keys = useRadioKeys(
    stages.map((s) => s.value),
    selected,
    setSelected,
  );
  void minutesFor;
  // Before a stage is chosen we can't promise a length, so the chip says
  // the range rather than a number that would be wrong for two of three.
  const timeLabel = "30–60 minutes";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="flex w-full flex-col items-center"
    >
      <WeKitLogo size="md" className="mb-4 lg:hidden" />

      <div className="grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-14">
        {/* LEFT: identity + compass */}
        <div className="flex flex-col items-center text-center">
          <WeKitLogo size="md" className="mb-6 hidden lg:block" />

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mb-4 lg:mb-6"
          >
            <div className="scale-[0.7] sm:scale-90 lg:scale-100">
              <ConstellationCompass reduce={reduce} />
            </div>
          </motion.div>

          {eyebrow && (
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
              style={{
                background: "rgba(0,102,91,0.16)",
                border: "1px solid rgba(0,166,147,0.35)",
                color: "#5EEAD4",
                fontSize: "11px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
              {eyebrow}
            </motion.div>
          )}

          <h1
            className="font-semibold tracking-tight text-white"
            style={{ fontSize: "clamp(24px, 5vw, 34px)", lineHeight: 1.12, letterSpacing: "-0.02em" }}
          >
            {title ?? "Welcome to WeKIT"}
          </h1>
          <p
            className="mt-2 max-w-md lg:mt-3"
            style={{ color: "#8B949E", fontSize: "14px", lineHeight: 1.55 }}
          >
            {subtitle ??
              "Let's discover the work you were made for. Choose the stage of life you're in — we'll personalize every step of your journey."}
          </p>


          {/* Trust indicators (desktop only here) */}
          <ul
            className="mt-6 hidden w-full flex-wrap items-center justify-center gap-x-5 gap-y-2 lg:flex"
            aria-label="What to expect"
          >
            <TrustItem icon={<Sparkles className="h-3.5 w-3.5" />} label={timeLabel} />
            <TrustItem icon={<ShieldCheck className="h-3.5 w-3.5" />} label="Private & Secure" />
            <TrustItem icon={<Users className="h-3.5 w-3.5" />} label="AI + Human Mentors" />
          </ul>
        </div>

        {/* RIGHT: choices + CTA */}
        <div className="flex w-full max-w-[480px] flex-col lg:max-w-none">
          <div
            role="radiogroup"
            aria-label="Choose your life stage"
            className="flex w-full flex-col gap-2.5"
          >
            {stages.map((stage, i) => {
              const isSel = selected === stage.value;
              return (
                <motion.button
                  key={stage.value}
                  ref={keys.setRef(i)}
                  role="radio"
                  aria-checked={isSel}
                  tabIndex={keys.tabIndexFor(i)}
                  onKeyDown={keys.onKeyDown(i)}
                  aria-label={`${stage.title}, ${stage.age}. ${stage.description}`}
                  initial={reduce ? false : { opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.05, duration: 0.4 }}
                  whileTap={reduce ? undefined : { scale: 0.99 }}
                  onClick={() => setSelected(stage.value)}
                  className="group relative w-full overflow-hidden text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1117]"
                  style={{
                    borderRadius: "18px",
                    padding: "14px 16px",
                    minHeight: "76px",
                    background: isSel
                      ? "linear-gradient(180deg, rgba(0,102,91,0.18) 0%, rgba(21,26,34,0.85) 100%)"
                      : "rgba(21, 26, 34, 0.65)",
                    border: `1.5px solid ${isSel ? "#00665B" : "rgba(255,255,255,0.06)"}`,
                    boxShadow: isSel
                      ? "0 0 0 4px rgba(0,102,91,0.18), 0 12px 32px -12px rgba(0,102,91,0.45)"
                      : "0 4px 14px -6px rgba(0,0,0,0.5)",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    transform: isSel && !reduce ? "scale(1.015)" : "scale(1)",
                    // @ts-expect-error custom CSS var for focus ring color
                    "--tw-ring-color": "#00665B",
                  }}
                >
                  <div className="flex items-start gap-3.5">
                    <div
                      className="flex h-11 w-11 shrink-0 items-center justify-center transition-colors duration-200"
                      style={{
                        borderRadius: "13px",
                        backgroundColor: isSel
                          ? "rgba(0,102,91,0.22)"
                          : "rgba(255,255,255,0.04)",
                        color: isSel ? "#5EEAD4" : "#8B949E",
                      }}
                      aria-hidden="true"
                    >
                      {stage.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-semibold text-white" style={{ fontSize: "16px" }}>
                          <span className="mr-1.5" aria-hidden="true">{stage.emoji}</span>
                          {stage.title}
                        </span>
                        <span style={{ color: "#8B949E", fontSize: "12px" }}>{stage.age}</span>
                      </div>
                      <p className="mt-1" style={{ color: "#8B949E", fontSize: "13px", lineHeight: 1.45 }}>
                        {stage.description}
                      </p>
                    </div>
                    <div
                      className="ml-1 mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-all duration-200"
                      style={{
                        border: `1.5px solid ${isSel ? "#00665B" : "rgba(255,255,255,0.18)"}`,
                        backgroundColor: isSel ? "#00665B" : "transparent",
                      }}
                      aria-hidden="true"
                    >
                      {isSel && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Trust indicators (mobile/tablet) */}
          <ul
            className="mt-4 flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 lg:hidden"
            aria-label="What to expect"
          >
            <TrustItem icon={<Sparkles className="h-3.5 w-3.5" />} label={timeLabel} />
            <TrustItem icon={<ShieldCheck className="h-3.5 w-3.5" />} label="Private & Secure" />
            <TrustItem icon={<Users className="h-3.5 w-3.5" />} label="AI + Human Mentors" />
          </ul>

          <div className="mt-4 w-full lg:mt-6">
            <Button
              onClick={onContinue}
              disabled={!selected}
              aria-disabled={!selected}
              className="h-13 w-full text-base font-semibold transition-all duration-200 disabled:cursor-not-allowed"
              style={{
                height: "52px",
                borderRadius: "16px",
                background: selected
                  ? "linear-gradient(135deg, #00665B 0%, #008A7A 100%)"
                  : "rgba(255,255,255,0.06)",
                color: selected ? "#FFFFFF" : "rgba(255,255,255,0.45)",
                boxShadow: selected
                  ? "0 12px 32px -10px rgba(0,138,122,0.55), inset 0 1px 0 rgba(255,255,255,0.12)"
                  : "none",
                opacity: selected ? 1 : 0.7,
              }}
            >
              Begin My Journey
              <ChevronRight className="ml-0.5 h-5 w-5" />
            </Button>
            {!selected && (
              <p className="mt-2 text-center" style={{ color: "#8B949E", fontSize: "12px" }}>
                Choose a life stage to begin
              </p>
            )}
            {footer && <div className="mt-4">{footer}</div>}
          </div>

        </div>
      </div>
    </motion.div>
  );
}

/* ───────────────── Persona Screen (Trailblazers) ───────────────── */

function PersonaScreen({
  persona,
  setPersona,
  onBack,
  onContinue,
  reduce,
}: {
  persona: string | null;
  setPersona: (v: string) => void;
  onBack: () => void;
  onContinue: () => void;
  reduce: boolean;
}) {
  // Two columns, so up/down moves a row and left/right moves one cell.
  const keys = useRadioKeys(TRAILBLAZER_PERSONAS, persona, setPersona, 2);

  return (
    <motion.div
      initial={{ opacity: 0, x: reduce ? 0 : 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className="flex w-full flex-col"
    >
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onBack}
          aria-label="Back to life stage selection"
          className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00665B]"
          style={{ color: "#8B949E" }}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <WeKitLogo size="sm" />
        <div className="h-10 w-10" aria-hidden="true" />
      </div>

      <h1
        className="font-semibold tracking-tight text-white"
        style={{ fontSize: "26px", lineHeight: 1.2, letterSpacing: "-0.02em" }}
      >
        Which best describes you today?
      </h1>
      <p
        className="mt-2"
        style={{ color: "#8B949E", fontSize: "14.5px", lineHeight: 1.55 }}
      >
        We'll tailor your journey to where you are right now.
      </p>

      <div
        role="radiogroup"
        aria-label="Choose what describes you today"
        className="mt-6 grid grid-cols-2 gap-2.5"
      >
        {TRAILBLAZER_PERSONAS.map((p, i) => {
          const isSel = persona === p;
          return (
            <motion.button
              key={p}
              ref={keys.setRef(i)}
              role="radio"
              aria-checked={isSel}
              tabIndex={keys.tabIndexFor(i)}
              onKeyDown={keys.onKeyDown(i)}
              initial={reduce ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.04 * i, duration: 0.3 }}
              onClick={() => setPersona(p)}
              className="text-left text-sm font-medium text-white transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00665B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1117]"
              style={{
                borderRadius: "16px",
                padding: "14px 14px",
                minHeight: "60px",
                background: isSel
                  ? "linear-gradient(180deg, rgba(0,102,91,0.22) 0%, rgba(21,26,34,0.9) 100%)"
                  : "rgba(21, 26, 34, 0.65)",
                border: `1.5px solid ${isSel ? "#00665B" : "rgba(255,255,255,0.06)"}`,
                boxShadow: isSel
                  ? "0 0 0 3px rgba(0,102,91,0.18)"
                  : "0 2px 10px -4px rgba(0,0,0,0.5)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
              }}
            >
              {p}
            </motion.button>
          );
        })}
      </div>

      <Button
        onClick={onContinue}
        disabled={!persona}
        aria-disabled={!persona}
        className="mt-8 h-14 w-full text-base font-semibold transition-all duration-200 disabled:cursor-not-allowed"
        style={{
          borderRadius: "16px",
          background: persona
            ? "linear-gradient(135deg, #00665B 0%, #008A7A 100%)"
            : "rgba(255,255,255,0.06)",
          color: persona ? "#FFFFFF" : "rgba(255,255,255,0.45)",
          boxShadow: persona
            ? "0 12px 32px -10px rgba(0,138,122,0.55), inset 0 1px 0 rgba(255,255,255,0.12)"
            : "none",
          opacity: persona ? 1 : 0.7,
        }}
      >
        Continue
        <ChevronRight className="ml-0.5 h-5 w-5" />
      </Button>
    </motion.div>
  );
}

/* ───────────────── Sub-components ───────────────── */

function TrustItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <li
      className="inline-flex items-center gap-1.5"
      style={{ color: "#8B949E", fontSize: "12px" }}
    >
      <span aria-hidden="true" style={{ color: "#00A693" }}>
        {icon}
      </span>
      <span>{label}</span>
    </li>
  );
}

function ConstellationCompass({ reduce }: { reduce: boolean }) {
  // Abstract compass surrounded by a soft constellation — purpose, discovery, possibility.
  return (
    <svg
      width="148"
      height="148"
      viewBox="0 0 148 148"
      fill="none"
      role="img"
      aria-label="A glowing compass surrounded by a constellation"
    >
      <defs>
        <radialGradient id="cc-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#00A693" stopOpacity="0.45" />
          <stop offset="55%" stopColor="#00665B" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#00665B" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cc-needle" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF5F1F" />
          <stop offset="100%" stopColor="#00A693" />
        </linearGradient>
      </defs>

      {/* Glow */}
      <circle cx="74" cy="74" r="62" fill="url(#cc-glow)" />

      {/* Outer ring */}
      <circle cx="74" cy="74" r="42" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
      <circle cx="74" cy="74" r="34" stroke="rgba(0,166,147,0.45)" strokeWidth="1" />

      {/* Tick marks */}
      {Array.from({ length: 12 }).map((_, i) => {
        const a = (i * Math.PI * 2) / 12;
        const x1 = 74 + Math.cos(a) * 40;
        const y1 = 74 + Math.sin(a) * 40;
        const x2 = 74 + Math.cos(a) * 44;
        const y2 = 74 + Math.sin(a) * 44;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="rgba(255,255,255,0.35)"
            strokeWidth="1"
            strokeLinecap="round"
          />
        );
      })}

      {/* Compass needle */}
      <motion.g
        style={{ originX: "74px", originY: "74px" }}
        animate={reduce ? undefined : { rotate: [0, 8, -4, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <polygon points="74,42 70,74 78,74" fill="url(#cc-needle)" />
        <polygon points="74,106 70,74 78,74" fill="rgba(255,255,255,0.25)" />
        <circle cx="74" cy="74" r="3" fill="#FFFFFF" />
      </motion.g>

      {/* Constellation stars */}
      {[
        [22, 28],
        [128, 36],
        [16, 88],
        [134, 100],
        [44, 132],
        [110, 130],
        [12, 60],
        [136, 70],
      ].map(([x, y], i) => (
        <motion.circle
          key={i}
          cx={x}
          cy={y}
          r={i % 2 === 0 ? 1.6 : 1.1}
          fill="#FFFFFF"
          opacity={0.7}
          animate={reduce ? undefined : { opacity: [0.3, 0.9, 0.3] }}
          transition={{ duration: 3 + (i % 3), repeat: Infinity, delay: i * 0.3 }}
        />
      ))}

      {/* Faint connecting lines */}
      <path
        d="M22 28 L44 132 M128 36 L134 100 M16 88 L12 60"
        stroke="rgba(255,255,255,0.10)"
        strokeWidth="0.75"
        fill="none"
      />
    </svg>
  );
}
